import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout, DatePicker, Tooltip, Select } from 'antd';
import * as echarts from 'echarts';
import dayjs from 'dayjs';
import _ from 'lodash';
import { fetchOperationStatActiveUserCount, fetchOperationStatActionCount } from '@services/Enterprise';
import './OperationStat.less';
import PageHeader from '@components/PageHeader/PageHeader';
const { RangePicker } = DatePicker;

export default function OperationStat() {
    const [activeUserCountDateRangeType, setActiveUserCountDateRangeType] = useState('7d');
    const [activeUserCountParams, setActiveUserCountParams] = useState([dayjs().subtract(7, 'days'), dayjs()]);
    const [activeUserCountData, setActiveUserCountData] = useState([]);

    const [actionCountDateRangeType, setActionCountDateRangeType] = useState('7d');
    const [actionCountParams, setActionCountParams] = useState([dayjs().subtract(7, 'days'), dayjs()]);
    const [actionCountData, setActionCountData] = useState([]);

    const changeDateRangeType = (type, value) => {
        let dateRange = [];
        switch (value) {
            case 'today':
                dateRange = [dayjs(), dayjs()];
                break;
            case 'yesterday':
                dateRange = [dayjs().subtract(1, 'days'), dayjs().subtract(1, 'days')];
                break;
            case '7d':
                dateRange = [dayjs().subtract(7, 'days'), dayjs()];
                break;
            case '14d':
                dateRange = [dayjs().subtract(14, 'days'), dayjs()];
                break;
            case '30d':
                dateRange = [dayjs().subtract(30, 'days'), dayjs()];
                break;
            case 'customized':
                dateRange = type === 'activeUser' ? activeUserCountParams : type === 'actionCount' ? actionCountParams : [];
                break;
            default:
                break;
        }

        if (type === 'activeUser') {
            setActiveUserCountParams(dateRange);
        }

        if (type === 'actionCount') {
            setActionCountParams(dateRange);
        }
    };

    const onChangeActiveUserCountDateRangeType = value => {
        changeDateRangeType('activeUser', value);
        setActiveUserCountDateRangeType(value);
    };

    const onChangeActionCountDateRangeType = value => {
        changeDateRangeType('actionCount', value);
        setActionCountDateRangeType(value);
    };

    const onChangeActiveUserCountDateRange = values => {
        setActiveUserCountDateRangeType('customized');
        setActiveUserCountParams(values);
    };

    const onChangeActionCountDateRange = values => {
        setActionCountDateRangeType('customized');
        setActionCountParams(values);
    };

    const getOperationStatActivityCountData = async () => {
        if (activeUserCountParams && activeUserCountParams.length) {
            const res = await fetchOperationStatActiveUserCount({
                beginTime: activeUserCountParams[0].format('YYYY-MM-DD'),
                endTime: activeUserCountParams[1].format('YYYY-MM-DD'),
            });
            if (res.status_code === 200) {
                setActiveUserCountData(res.data);
            }
        }
    };

    const getOperationStatActionCountData = async () => {
        if (actionCountParams && actionCountParams.length) {
            const res = await fetchOperationStatActionCount({
                beginTime: actionCountParams[0].format('YYYY-MM-DD'),
                endTime: actionCountParams[1].format('YYYY-MM-DD'),
            });
            if (res.status_code === 200) {
                setActionCountData(res.data);
            }
        }
    };

    useEffect(() => {
        getOperationStatActivityCountData();
    }, [activeUserCountParams]);

    useEffect(() => {
        getOperationStatActionCountData();
    }, [actionCountParams]);

    let activeUserChart = null;
    const drawActiveUserChart = () => {
        activeUserChart && activeUserChart.dispose();

        activeUserChart = echarts.init(document.getElementById('chart-active-user-count'));
        const option = {
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'shadow',
                },
            },
            legend: {},
            grid: {
                left: '5%',
                top: '15%',
                right: '5%',
                bottom: '5%',
                containLabel: true,
            },
            xAxis: {
                type: 'category',
                data: _.map(activeUserCountData, 'date').map(d => dayjs(d).format('M月D日')),
                boundaryGap: false,
            },
            yAxis: {
                name: '活跃人数',
                type: 'value',
                boundaryGap: false,
            },
            series: [
                {
                    data: _.map(activeUserCountData, 'counts'),
                    type: 'line',
                    label: {
                        show: true,
                        position: 'top',
                    },
                    lineStyle: {
                        width: 1,
                    },
                    areaStyle: {
                        opacity: 0.8,
                        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                            {
                                offset: 0,
                                color: 'rgb(0, 221, 255)',
                            },
                            {
                                offset: 1,
                                color: 'rgb(77, 119, 255)',
                            },
                        ]),
                    },
                    emphasis: {
                        focus: 'series',
                    },
                    smooth: true,
                },
            ],
        };
        activeUserChart.setOption(option);

        window.addEventListener('resize', () => {
            activeUserChart.resize();
        });
    };

    useEffect(() => {
        if (activeUserCountData && activeUserCountData.length) {
            drawActiveUserChart();
        }
    }, [activeUserCountData]);

    let actionCountChart = null;
    const drawActionCountChart = () => {
        actionCountChart && actionCountChart.dispose();

        actionCountChart = echarts.init(document.getElementById('chart-action-count'));

        _.reverse(actionCountData);

        const option = {
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'shadow',
                },
            },
            legend: {},
            grid: {
                left: '5%',
                top: 50,
                right: '10%',
                bottom: 50,
                containLabel: true,
            },
            xAxis: {
                name: '活跃次数',
                type: 'value',
                boundaryGap: [0, 0.01],
                position: 'top',
            },
            yAxis: {
                type: 'category',
                data: _.map(actionCountData, 'staff_name'),
            },
            visualMap: {
                orient: 'horizontal',
                left: 'center',
                min: 10,
                max: 100,
                text: ['高频', '底频'],
                // Map the score column to color
                dimension: 0,
                inRange: {
                    color: ['#65B581', '#FFCE34', '#FD665F'],
                },
            },
            series: [
                {
                    type: 'bar',
                    label: {
                        show: true,
                        position: 'right',
                    },
                    data: _.map(actionCountData, 'counts'),
                },
            ],
        };
        actionCountChart.setOption(option);

        actionCountChart.resize({ height: 50 * actionCountData.length + 50 });

        window.addEventListener('resize', () => {
            actionCountChart.resize();
        });
    };

    useEffect(() => {
        console.log(actionCountData);
        if (actionCountData && actionCountData.length) {
            drawActionCountChart();
        }
    }, [actionCountData]);

    const disabledDate = current => current > dayjs().endOf('day');

    return (
        <>
            <PageHeader breadcrumb={[]}>
                <Link to="/enterprise/operation/log" className="back">
                    <i className="bi bi-chevron-left"></i>
                </Link>
                <span className="current-title">后台操作统计</span>
            </PageHeader>

            <div className="page-route-body no-bg-color">
                <div className="content-block">
                    <div className="content-block-head">
                        <div className="content-block-title">
                            活跃人数趋势
                            <Tooltip title="按天统计活跃人数，活跃即后台产生查询或增删改等操作">
                                <span className="tooltip-icon">
                                    <i className="bi bi-question-circle-fill"></i>
                                </span>
                            </Tooltip>
                        </div>
                        <div className="content-block-toolbar">
                            <Select placeholder="统计时段" style={{ width: 120, marginRight: 8 }} value={activeUserCountDateRangeType} onChange={onChangeActiveUserCountDateRangeType}>
                                <Select.Option value="today">今天</Select.Option>
                                <Select.Option value="yesterday">昨天</Select.Option>
                                <Select.Option value="7d">最近7天</Select.Option>
                                <Select.Option value="14d">最近14天</Select.Option>
                                <Select.Option value="30d">最近30天</Select.Option>
                                <Select.Option value="customized">自定义</Select.Option>
                            </Select>
                            <RangePicker style={{ width: 240 }} value={activeUserCountParams} format="YYYY-MM-DD" onChange={onChangeActiveUserCountDateRange} disabledDate={disabledDate} />
                        </div>
                    </div>

                    <div className="content-block-body">
                        <div id="chart-active-user-count" className="chart-active-user-count" style={{ width: '100%', height: 400 }} />
                    </div>
                </div>

                <div className="content-block">
                    <div className="content-block-head">
                        <div className="content-block-title">
                            活跃次数分析
                            <Tooltip title="统计指定时间范围后台管理员产生查询或增删改等操作次数，倒序排列">
                                <span className="tooltip-icon">
                                    <i className="bi bi-question-circle-fill"></i>
                                </span>
                            </Tooltip>
                        </div>
                        <div className="content-block-toolbar">
                            <Select placeholder="统计时段" style={{ width: 120, marginRight: 8 }} value={actionCountDateRangeType} onChange={onChangeActionCountDateRangeType}>
                                <Select.Option value="today">今天</Select.Option>
                                <Select.Option value="yesterday">昨天</Select.Option>
                                <Select.Option value="7d">最近7天</Select.Option>
                                <Select.Option value="14d">最近14天</Select.Option>
                                <Select.Option value="30d">最近30天</Select.Option>
                                <Select.Option value="customized">自定义</Select.Option>
                            </Select>
                            <RangePicker style={{ width: 240 }} value={actionCountParams} format="YYYY-MM-DD" onChange={onChangeActionCountDateRange} disabledDate={disabledDate} />
                        </div>
                    </div>

                    <div className="content-block-body">
                        <div id="chart-action-count" className="chart-action-count" style={{ width: '100%', minHeight: 400 }} />
                    </div>
                </div>
            </div>
        </>
    );
}
